import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Button,
    Select,
    MenuItem,
    CircularProgress,
    FormGroup,
    FormControlLabel,
    Checkbox,
    InputLabel,
    IconButton,
    FormControl
} from "@mui/material";
import axios from "axios";
import { BACKEND_BASE_URL } from "../../config";
import { Add, Remove } from "@mui/icons-material";
import { FormHelperText, FormLabel, Radio, RadioGroup } from "@material-ui/core";
import { toast } from "react-toastify"; // Import toast if using react-toastify


export default function EditDataForm({ open, onSubmit, editData, onClose, api }) {

    const [isInitializing, setIsInitializing] = useState(true);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        id: "",
        country: "",
        state: "",
        county: "",
        municipality_name: "",
        latitude: "",
        longitude: "",
        population: "",
        channel_type: "",
        boe_channels: [{ url: "", boe_website: "", playlists: [] }],
        town_channels: [{ url: "", town_website: "", playlists: [] }],

    });




    const fetchChannelId = async (url) => {
        try {

            const response = await axios.get(`${BACKEND_BASE_URL}/api/fetch-channel-id/`, {
                params: { url }
            });

            return response.data?.channel_id;
        } catch (error) {
            console.error("Error fetching channel ID:", error);
            return null;
        }
    };

    const handleChannelTypeChange = (e) =>
        setFormData((prev) => ({ ...prev, channel_type: e?.target?.value || "" }));





    const fetchPlaylists = async (channel_id) => {
        try {
            setLoading(true);
            const response = await axios.get(`${BACKEND_BASE_URL}/api/fetch-playlists/`, {
                params: { channel_id, api_key: api }
            });

            setLoading(false);
            return response.data?.playlists || [];

        } catch (error) {
            setLoading(false);

            let errorMessage =
                error.response?.data?.error || "Error fetching playlists:";

            // Custom message for YouTube quota exceeded
            if (error.response?.status === 400) {
                errorMessage = error.response?.data?.error;
            }
            // Custom message for YouTube quota exceeded
            else if (error.response?.status === 429) {
                errorMessage = error.response?.data?.error;
            }
            else if (error.response?.status === 401) {
                errorMessage = error.response?.data?.error;
            }
            toast.error(`${errorMessage}`);
            console.error("Error fetching playlists:", error);
            return [];
        }
    };


    // Fetch all Playlists when the form opens
    useEffect(() => {
        const initializeForm = async () => {
            console.log("Initializing form with editData:", editData);
            if (open && editData) {
                setLoading(true);

                const newFormData = {
                    id: editData?.id || "",
                    country: editData?.country || "",
                    state: editData?.state || "",
                    county: editData?.county?.trim() || "",
                    municipality_name: editData?.municipality?.trim() || "",
                    latitude: editData?.latitude,
                    longitude: editData?.longitude,
                    population: editData?.population,
                    channel_type: editData?.channel_type || "",
                    boe_channels: [],
                    town_channels: []
                };

                // Process BOE Channels
                if (editData?.boe?.channels) {
                    for (const channel of editData?.boe?.channels) {
                        const channelId = await fetchChannelId(channel.boe_url);
                        const playlists = channelId ? await fetchPlaylists(channelId) : [];
                        newFormData.boe_channels.push({
                            url: channel?.boe_url || "",
                            boe_website: channel?.boe_website || "",
                            playlists,
                            selectedPlaylists: [] // Initialize selectedPlaylists
                        });
                    }
                }

                // Process Town Channels
                if (editData?.town?.channels) {
                    for (const channel of editData?.town?.channels) {
                        const channelId = await fetchChannelId(channel.town_url);
                        const playlists = channelId ? await fetchPlaylists(channelId) : [];
                        newFormData?.town_channels.push({
                            url: channel?.town_url || "",
                            town_website: channel?.town_website || "",
                            playlists,
                            selectedPlaylists: [] // Initialize selectedPlaylists
                        });
                    }
                }



                setFormData(newFormData);
                setLoading(false);
                setIsInitializing(false);
            }
        };

        initializeForm();
    }, [open, editData]);

    const handleUrlInputChange = (index, type, value) => {
        let updatedChannels = [...formData[type]];
        updatedChannels[index].url = value;

        setFormData(prev => ({
            ...prev,
            [type]: updatedChannels
        }));
    };


    // Handle URL Change and Fetch Playlists
    const handleUrlChange = async (index, type, value, websiteType) => {

        let updatedChannels = [...formData[type]];
        updatedChannels[index].url = value;

        if (value) {

            const channelId = await fetchChannelId(value);

            if (channelId) {
                const playlists = await fetchPlaylists(channelId);
                updatedChannels[index].playlists = playlists;
            }
        } else {
            updatedChannels[index].playlists = [];
        }

        setFormData(prev => ({ ...prev, [type]: updatedChannels }));
    };

    // Handle Website URL Change
    const handleWebsiteChange = (index, type, value, websiteType) => {
        let updatedChannels = [...formData[type]];
        if (websiteType === "boe") {
            updatedChannels[index].boe_website = value;
        } else {
            updatedChannels[index].town_website = value;
        }
        setFormData(prev => ({ ...prev, [type]: updatedChannels }));
    };

    // Handle Playlist Selection
    const handlePlaylistChange = (index, type, selectedPlaylistIds) => {
        let updatedChannels = [...formData[type]];

        // Ensure playlists array exists
        if (!updatedChannels[index].playlists) {
            updatedChannels[index].playlists = [];
        }

        // Keep static options
        const staticOptions = [
            { id: "whole-channel", title: "Whole Channel", url: "" },
            { id: "live-channel", title: "Live Channel", url: "" }
        ];

        // Filter selected playlists and include static options if selected
        updatedChannels[index].selectedPlaylists = [
            ...updatedChannels[index].playlists.filter(playlist =>
                selectedPlaylistIds.includes(playlist.id)
            ),
            ...staticOptions.filter(option => selectedPlaylistIds.includes(option?.id))
        ];

        setFormData(prev => ({ ...prev, [type]: updatedChannels }));
    };

    // Add New Channel
    const addNewChannel = (type) => {
        setFormData((prev) => ({
            ...prev,
            [type]: [...prev[type], { url: "", boe_website: "", town_website: "" }],
        }));
    };

    // Remove Channel
    const removeChannel = (index, type) => {
        setFormData((prev) => ({
            ...prev,
            [type]: prev[type].filter((_, i) => i !== index),
        }));
    };


    const validateFields = () => {

        let newErrors = {};
        if (!formData.country) newErrors.state = "Country is required";
        // if (!formData.state) newErrors.state = "State is required";
        if (!formData.county) newErrors.county = "County is required";
        if (!formData.municipality_name) newErrors.municipality_name = "Town is required";
        if (!formData.channel_type) newErrors.channel_type = "Channel type is required";

        // Validate BOE Channels if selected
        if (formData?.channel_type === "Board of Education" || formData?.channel_type === "Both") {
            formData.boe_channels.forEach((channel, index) => {
                if (!channel.url) newErrors[`boe_url_${index}`] = "BOE Youtube Channel URL is required";
                if (channel?.selectedPlaylists?.length === 0) newErrors[`boe_playlists_${index}`] = "At least one playlist is required";
            });
        }

        // Validate Town Channels if selected
        if (formData?.channel_type === "Town Government" || formData?.channel_type === "Both") {
            formData.town_channels.forEach((channel, index) => {
                if (!channel.url) newErrors[`town_url_${index}`] = "Town Youtube Channel URL is required";
                if (channel?.selectedPlaylists?.length === 0) newErrors[`town_playlists_${index}`] = "At least one playlist is required";
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleSave = async (id) => {

        if (!validateFields()) return;
        setLoading(true);
        try {
            const payload = {
                ...formData,

            };



            if (formData?.channel_type === "Board of Education" || formData?.channel_type === "Both") {

                payload.boe_channels = formData.boe_channels.map(channel => ({
                    url: channel?.url,
                    boe_website: channel?.boe_website,
                    selectedPlaylists: channel.selectedPlaylists.map(pl => ({
                        playlist_id: pl?.id,
                        playlist_name: pl?.title,
                        playlist_url: (pl?.id === "whole-channel" || pl?.id === "live-channel") ? channel?.url : pl?.url
                    }))
                }));
            }
            else {
                // Ensure boe_channels is completely removed
                delete payload?.boe_channels;
            }

            if (formData?.channel_type === "Town Government" || formData?.channel_type === "Both") {
                payload.town_channels = formData.town_channels.map(channel => ({
                    url: channel?.url,
                    town_website: channel?.town_website,
                    selectedPlaylists: channel.selectedPlaylists.map(pl => ({
                        playlist_id: pl?.id,
                        playlist_name: pl?.title,
                        playlist_url: (pl?.id === "whole-channel" || pl?.id === "live-channel") ? channel?.url : pl?.url
                    }))
                }));
            }
            else {
                // Ensure town_channels is completely removed
                delete payload?.town_channels;
            }


            const response = await axios.post(`${BACKEND_BASE_URL}/api/save-channel-data/`, { ...payload, api_key: api });

            // Extract IDs for deletion


            // Step 2: Call the DELETE API for each ChannelData ID
            if (response.data) {
                await axios.delete(`${BACKEND_BASE_URL}/api/delete-channel-data/${id}/`, {
                    params: {
                        channel_type: payload?.channel_type
                    }
                });


            }


            onSubmit(payload);
        } catch (error) {
            if (error?.response && error?.response?.data && error.response?.data?.message) {
                toast.error(error?.response?.data?.message || "An unexpected error occurred. Please try again."); // Show error message in toast
            } else {
                toast.error("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };



    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Edit Data</DialogTitle>
            <DialogContent style={{ maxHeight: "500px", overflowY: "auto" }}>
                {isInitializing ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                        <CircularProgress size={40} />
                    </div>
                ) : (
                    <>
                        <TextField margin="dense" label="Country" error={!!errors.country} fullWidth value={formData?.country} helperText={errors?.country} onChange={(e) => setFormData({ ...formData, country: e?.target?.value })} />
                        <TextField margin="dense" label="State" error={!!errors.state} fullWidth value={formData?.state} helperText={errors?.state} onChange={(e) => setFormData({ ...formData, state: e?.target?.value })} />
                        <TextField margin="dense" label="County" error={!!errors.county} fullWidth value={formData?.county} helperText={errors?.county} onChange={(e) => setFormData({ ...formData, county: e?.target?.value })} />
                        <TextField margin="dense" label="Town" fullWidth error={!!errors.municipality_name} value={formData?.municipality_name} helperText={errors?.municipality_name} onChange={(e) => setFormData({ ...formData, municipality_name: e?.target?.value })} />
                        <TextField margin="dense" label="Latitude" fullWidth value={formData?.latitude} disabled />
                        <TextField margin="dense" label="Longitude" fullWidth value={formData?.longitude} disabled />
                        <TextField margin="dense" label="Population" fullWidth value={formData?.population} disabled />
                        <FormControl component="fieldset" margin="dense" error={!!errors.channel_type} >
                            <FormLabel component="legend">Channel Type</FormLabel>
                            <RadioGroup row value={formData?.channel_type} onChange={handleChannelTypeChange}>
                                <FormControlLabel value="Board of Education" control={<Radio />} label="Board of Education" />
                                <FormControlLabel value="Town Government" control={<Radio />} label="Town Government" />
                                <FormControlLabel value="Both" control={<Radio />} label="Both" />
                            </RadioGroup>
                            {errors?.channel_type && <FormHelperText>{errors?.channel_type}</FormHelperText>}
                        </FormControl>

                        {/* BOE Section */}
                        {(formData?.channel_type === "Board of Education" || formData?.channel_type === "Both") && (<><h4>Board of Education</h4>

                            {formData.boe_channels.map((channel, index) => (
                                <div key={index} style={{ marginBottom: "10px" }}>
                                    <TextField
                                        margin="dense"
                                        label={`BOE Youtube Channel URL ${index + 1}`}
                                        fullWidth
                                        value={channel?.url}
                                        error={!!errors[`boe_url_${index}`]}
                                        helperText={errors[`boe_url_${index}`]}
                                        onBlur={(e) => handleUrlChange(index, "boe_channels", e?.target?.value, "boe")}
                                        onChange={(e) => handleUrlInputChange(index, "boe_channels", e?.target?.value, "boe")}
                                    />
                                    <TextField
                                        margin="dense"
                                        label={`BOE Website ${index + 1}`}
                                        fullWidth
                                        value={channel?.boe_website}

                                        onChange={(e) => handleWebsiteChange(index, "boe_channels", e?.target?.value, "boe")}
                                    />

                                    <FormControl fullWidth margin="dense" error={!!errors[`boe_playlists_${index}`]}
                                    >
                                        <InputLabel>BOE Playlists</InputLabel>
                                        <Select
                                            multiple
                                            value={(channel?.selectedPlaylists || []).map(pl => pl?.id)}
                                            onChange={(e) => handlePlaylistChange(index, "boe_channels", e?.target?.value)}
                                            renderValue={(selected) => {
                                                const selectedNames = (channel?.playlists || [])
                                                    .filter(playlist => selected.includes(playlist?.id))
                                                    .map(playlist => playlist?.title)
                                                    .concat(selected.includes("whole-channel") ? ["Main Channel"] : [])
                                                    .concat(selected.includes("live-channel") ? ["Live Channel"] : [])
                                                    .join(", ");


                                                return <div style={{ whiteSpace: "normal", overflowWrap: "break-word" }}>{selectedNames}</div>;
                                            }}
                                            fullWidth
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300, // Limit dropdown height
                                                        overflowY: "auto",
                                                        width: 250 // Set proper width
                                                    }
                                                }
                                            }}
                                        >

                                            {/* Static options */}
                                            <MenuItem key="whole-channel" value="whole-channel">
                                                <Checkbox checked={(channel?.selectedPlaylists || []).some(pl => pl?.id === "whole-channel")} />
                                                Main Channel
                                            </MenuItem>
                                            <MenuItem key="live-channel" value="live-channel">
                                                <Checkbox checked={(channel?.selectedPlaylists || []).some(pl => pl?.id === "live-channel")} />
                                                Live Channel
                                            </MenuItem>
                                            {loading ? (
                                                <MenuItem disabled>
                                                    <CircularProgress size={20} style={{ marginRight: "10px" }} /> Loading...
                                                </MenuItem>
                                            ) : (
                                                (channel?.playlists || []).length > 0 ? (
                                                    (channel?.playlists || []).map((playlist) => (
                                                        <MenuItem key={playlist?.id} value={playlist?.id}>
                                                            <Checkbox checked={(channel?.selectedPlaylists || []).some(pl => pl?.id === playlist?.id)} />
                                                            {playlist?.title}
                                                        </MenuItem>
                                                    ))
                                                ) : (
                                                    <MenuItem disabled>No playlists available</MenuItem>
                                                ))}

                                        </Select>

                                        {errors[`boe_playlists_${index}`] && <FormHelperText style={{ color: "red" }}>{errors[`boe_playlists_${index}`]}</FormHelperText>}
                                    </FormControl>
                                    <Button onClick={() => removeChannel(index, "boe_channels")} style={{ color: "red" }}>Remove</Button>


                                </div>
                            ))}

                            <Button variant="contained" onClick={() => addNewChannel("boe_channels")}>+ Add Board of Education</Button>
                        </>)}


                        {/* Town Section */}
                        {(formData?.channel_type === "Town Government" || formData?.channel_type === "Both") && (<><h4>Town Government</h4>
                            {formData.town_channels.map((channel, index) => (
                                <div key={index} style={{ marginBottom: "10px" }}>
                                    <TextField
                                        margin="dense"
                                        label={`Town Youtube Channel URL ${index + 1}`}
                                        fullWidth
                                        value={channel?.url}
                                        error={!!errors[`town_url_${index}`]}
                                        helperText={errors[`town_url_${index}`]}
                                        onBlur={(e) => handleUrlChange(index, "town_channels", e?.target?.value, "town")}
                                        onChange={(e) => handleUrlInputChange(index, "town_channels", e?.target?.value, "town")}
                                    />
                                    <TextField
                                        margin="dense"
                                        label={`Town Website ${index + 1}`}
                                        fullWidth
                                        value={channel?.town_website}
                                        // error={!!errors[`town_website_${index}`]}
                                        // helperText={errors[`town_website_${index}`]}
                                        onChange={(e) => handleWebsiteChange(index, "town_channels", e?.target?.value, "town")}
                                    />

                                    <FormControl fullWidth margin="dense" error={!!errors[`town_playlists_${index}`]}
                                    >
                                        <InputLabel>Town Playlists</InputLabel>
                                        <Select
                                            multiple
                                            value={(channel.selectedPlaylists || []).map(pl => pl?.id)}
                                            onChange={(e) => handlePlaylistChange(index, "town_channels", e?.target?.value)}
                                            renderValue={(selected) => {
                                                const selectedNames = (channel?.playlists || [])
                                                    .filter(playlist => selected.includes(playlist?.id))
                                                    .map(playlist => playlist?.title)
                                                    .concat(selected.includes("whole-channel") ? ["Main Channel"] : [])
                                                    .concat(selected.includes("live-channel") ? ["Live Channel"] : [])
                                                    .join(", ");


                                                return <div style={{ whiteSpace: "normal", overflowWrap: "break-word" }}>{selectedNames}</div>;
                                            }}
                                            fullWidth
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300, // Limit dropdown height
                                                        overflowY: "auto",
                                                        width: 250 // Set proper width
                                                    }
                                                }
                                            }}
                                        >

                                            {/* Static options */}
                                            <MenuItem key="whole-channel" value="whole-channel">
                                                <Checkbox checked={(channel?.selectedPlaylists || []).some(pl => pl?.id === "whole-channel")} />
                                                Main Channel
                                            </MenuItem>
                                            <MenuItem key="live-channel" value="live-channel">
                                                <Checkbox checked={(channel?.selectedPlaylists || []).some(pl => pl?.id === "live-channel")} />
                                                Live Channel
                                            </MenuItem>
                                            {loading ? (
                                                <MenuItem disabled>
                                                    <CircularProgress size={20} style={{ marginRight: "10px" }} /> Loading...
                                                </MenuItem>
                                            ) : (
                                                (channel?.playlists || []).length > 0 ? (
                                                    (channel?.playlists || []).map((playlist) => (
                                                        <MenuItem key={playlist?.id} value={playlist?.id}>
                                                            <Checkbox checked={(channel?.selectedPlaylists || []).some(pl => pl?.id === playlist?.id)} />
                                                            {playlist?.title}
                                                        </MenuItem>
                                                    ))
                                                ) : (
                                                    <MenuItem disabled>No playlists available</MenuItem>
                                                ))}

                                        </Select>
                                        {errors[`town_playlists_${index}`] && <FormHelperText style={{ color: "red" }}>{errors[`town_playlists_${index}`]}</FormHelperText>}
                                    </FormControl>
                                    <Button onClick={() => removeChannel(index, "town_channels")} style={{ color: "red" }}>Remove</Button>






                                </div>

                            ))}
                            <Button variant="contained" onClick={() => addNewChannel("town_channels")}>+ Add Town Government</Button>
                        </>)}




                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} style={{ backgroundColor: "red", color: "white" }}>Cancel</Button>
                <Button style={{ backgroundColor: "#0B194A", color: "white" }} onClick={() => handleSave(formData.id)}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}
